import { Button, DialogActions, DialogContent, Stack } from '@mui/material'
import { FormApi } from 'final-form'
import { ReactElement, useCallback, useMemo, useState } from 'react'
import { Form } from 'react-final-form'
import { useMessageSource } from 'src/i18n/useMessageSource'
import {
  EvaluationFileItem,
  EvidenceOfEffectivenessFilesGrid,
} from 'src/screens/shared/evidence-of-effectiveness/EvidenceOfEffectivenessFilesGrid'
import { createNewEvaluationFileItem } from 'src/screens/shared/evidence-of-effectiveness/utils/EvidenceOfEffectivenessUtils'
import { SecondaryButton } from 'src/shared/button/Buttons'
import { FileUploadField } from 'src/shared/form/control/FileUploadField'
import { composeValidators, maxListLength, minListLength } from 'src/shared/form/validation/validators'
import { ModalDialog } from 'src/shared/modal-dialog/ModalDialog'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { Section } from 'src/shared/presentation/Section'

interface Props {
  open: boolean
  titleKey?: string
  helpAndInstructionsKey?: string
  setDirtyUploadModal: (dirty: boolean) => void
  initialValues: EvaluationFileItem[]
  initialDeletedItems: EvaluationFileItem[]
  maxFilesUpload?: number
  minFilesUpload?: number
  onCancel: () => void
  onSave: (fileItems: Array<EvaluationFileItem>, deletedFileItems: Array<EvaluationFileItem>) => void
}

interface FormValues {
  fileItems: Array<EvaluationFileItem>
  files: Array<File>
  deletedItems: Array<EvaluationFileItem>
}

export const EvidenceOfEffectivenessFileUploadModal = ({
  open,
  initialValues,
  initialDeletedItems,
  setDirtyUploadModal,
  titleKey = 'label.file.upload.modal.title',
  helpAndInstructionsKey,
  maxFilesUpload = 7,
  minFilesUpload = 0,
  onCancel,
  onSave,
}: Props): ReactElement => {
  const { getMessage } = useMessageSource()

  const [loading, setLoading] = useState<boolean>(false)

  // form submission and cancellation
  const onSaveLocal = useCallback(
    async ({ fileItems, deletedItems }: FormValues) => {
      try {
        setLoading(true)
        onSave(fileItems ?? [], deletedItems ?? [])
      } finally {
        setLoading(false)
        onCancel()
      }
    },
    [onSave, onCancel],
  )

  const onCancelLocal = useCallback(() => {
    setDirtyUploadModal(false)
    onCancel()
  }, [onCancel, setDirtyUploadModal])

  // handlers
  const handleDeleteFile = useCallback(
    (id: number, values: FormValues, form: any) => {
      const itemToDelete = values.fileItems.find((item) => item.id === id)

      if (itemToDelete) {
        if (itemToDelete.entityId !== null || itemToDelete.fileUUID !== null) {
          const currentDeletedItems = values.deletedItems || []
          form.change('deletedItems', [...currentDeletedItems, itemToDelete])
        }

        const updatedFiles = values.files.filter((file) => file !== itemToDelete.file)
        form.change('files', updatedFiles)

        const updatedFileItems = values.fileItems
          .filter((item) => item.id !== id)
          .map((item, index) => ({
            ...item,
            evaluationIndex: index + 1,
          }))
        form.change('fileItems', updatedFileItems)

        setDirtyUploadModal(true)
      }
    },
    [setDirtyUploadModal],
  )

  const handleAddFiles = useCallback(
    (newFilesAdded: File[], values: FormValues, form: FormApi<FormValues, Partial<FormValues>>) => {
      const newItems = newFilesAdded.map((file, index) =>
        createNewEvaluationFileItem(file, values.fileItems.length + index + 1),
      )

      if (newItems.length > 0) {
        form.change('fileItems', [...values.fileItems, ...newItems])
        setDirtyUploadModal(true)
      }
    },
    [setDirtyUploadModal],
  )

  const handleEditEvaluationFileItem = useCallback(
    (updatedItem: EvaluationFileItem, values: FormValues, form: FormApi<FormValues, Partial<FormValues>>) => {
      const updatedList = values.fileItems.map((item) => (item.id === updatedItem.id ? updatedItem : item))

      form.change('fileItems', updatedList)
      setDirtyUploadModal(true)
    },
    [setDirtyUploadModal],
  )

  // validators
  const validateForm = useCallback(
    (values: FormValues) => {
      const errors: any = {}

      if (values.fileItems.length < minFilesUpload) {
        errors.fileItems = getMessage('validation.list.min.length', [minFilesUpload])
        return errors
      }

      if (maxFilesUpload !== undefined && values.fileItems.length > maxFilesUpload) {
        errors.fileItems = getMessage('validation.list.max.length', [maxFilesUpload])
        return errors
      }

      const hasInvalidItems = values.fileItems?.some((item) => {
        const isNameInvalid = !item.name || item.name.trim() === ''
        const isAuthorInvalid = !item.author || item.author.trim() === ''
        const isYearInvalid = !item.yearPublication || item.yearPublication === 0

        return isNameInvalid || isAuthorInvalid || isYearInvalid
      })

      if (hasInvalidItems) {
        errors.fileItems = getMessage('validation.multiple-file.required.fields.missing')
      }

      return errors
    },
    [getMessage, maxFilesUpload, minFilesUpload],
  )

  // form inputs
  const formInitialValues = useMemo(
    () => ({
      fileItems: initialValues ?? [],
      files: initialValues.map((item) => item.file) ?? [],
      deletedItems: initialDeletedItems ?? [],
    }),
    [initialValues, initialDeletedItems],
  )

  const fileUploadValidation = useMemo(
    () => composeValidators(maxListLength(maxFilesUpload), minListLength(minFilesUpload)),
    [maxFilesUpload, minFilesUpload],
  )

  const fileUploadHelperText = useMemo(
    () =>
      maxFilesUpload ? getMessage('label.file.upload.helper.text.max-files-allowed', [maxFilesUpload]) : undefined,
    [getMessage, maxFilesUpload],
  )

  return (
    <>
      {open && (
        <ModalDialog
          open={open}
          onClose={onCancelLocal}
          title={getMessage(titleKey)}
          maxWidth="xl"
          withCloseIcon={true}
        >
          <Form<FormValues>
            onSubmit={onSaveLocal}
            validate={validateForm}
            initialValues={formInitialValues}
            render={({ handleSubmit, values, form, hasValidationErrors, errors }) => {
              const fileCount = values?.fileItems?.length ?? 0

              return (
                <form onSubmit={handleSubmit}>
                  <DialogContent>
                    <Section
                      id={'evidence-of-effectiveness-file-upload'}
                      helpAndInstructions={
                        helpAndInstructionsKey && (
                          <HelpAndInstructions
                            defaultExpansion
                            customDescriptionText={getMessage(helpAndInstructionsKey)}
                            labelKey={helpAndInstructionsKey}
                          />
                        )
                      }
                    >
                      <Stack spacing={10}>
                        <FileUploadField
                          name="files"
                          validate={fileUploadValidation}
                          onFilesChange={(files) => handleAddFiles(files, values, form)}
                          maxFiles={maxFilesUpload}
                          multiple={true}
                          helperText={fileUploadHelperText}
                        />

                        <EvidenceOfEffectivenessFilesGrid
                          loading={loading}
                          editMode={true}
                          showDeleteButton={true}
                          showPublishCheckbox={true}
                          showHeaderRequiredAsterisk={true}
                          rows={values.fileItems || []}
                          helperText={fileUploadHelperText}
                          onDeleteFile={(id) => handleDeleteFile(id, values, form)}
                          onUpdateFileItem={(updatedItem) => handleEditEvaluationFileItem(updatedItem, values, form)}
                          error={!!errors?.fileItems}
                          errorMessage={errors?.fileItems}
                        />
                      </Stack>
                    </Section>
                  </DialogContent>
                  <DialogActions>
                    <SecondaryButton messageKey="button.cancel" onClick={onCancelLocal} />
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      loading={loading}
                      disabled={fileCount < minFilesUpload || fileCount > maxFilesUpload || hasValidationErrors}
                    >
                      {getMessage('button.upload')}
                    </Button>
                  </DialogActions>
                </form>
              )
            }}
          />
        </ModalDialog>
      )}
    </>
  )
}
